import React, { useState, useRef, useEffect } from 'react';
import {
    Box,
    Typography,
    IconButton,
    useTheme,
    useMediaQuery,
    Rating
} from '@mui/material';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import CloseIcon from '@mui/icons-material/Close';

const MobileCardPreview = ({ cards, currentIndex, onClose, onIndexChange }) => {
    const [activeStep, setActiveStep] = useState(currentIndex);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const touchStartX = useRef(0);
    const touchStartY = useRef(0);

    useEffect(() => {
        setActiveStep(currentIndex);
    }, [currentIndex]);

    const handleNext = () => {
        if (activeStep < cards.length - 1) {
            const newIndex = activeStep + 1;
            setActiveStep(newIndex);
            onIndexChange?.(newIndex);
        }
    };

    const handleBack = () => {
        if (activeStep > 0) {
            const newIndex = activeStep - 1;
            setActiveStep(newIndex);
            onIndexChange?.(newIndex);
        }
    };

    const handleTouchStart = (e) => {
        touchStartX.current = e.touches[0].clientX;
        touchStartY.current = e.touches[0].clientY;
    };

    const handleTouchEnd = (e) => {
        const touchEndX = e.changedTouches[0].clientX;
        const touchEndY = e.changedTouches[0].clientY;
        const diffX = touchStartX.current - touchEndX;
        const diffY = touchStartY.current - touchEndY;

        // Only handle horizontal swipes (ignore vertical swipes)
        if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 50) {
            if (diffX > 0) {
                // Swipe left - next card
                handleNext();
            } else {
                // Swipe right - previous card
                handleBack();
            }
        }
    };

    if (!isMobile || !cards || cards.length === 0) {
        return null;
    }

    const currentCard = cards[activeStep];

    return (
        <Box
            sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.98)',
                backdropFilter: 'blur(10px)',
                zIndex: 9999,
                display: 'flex',
                flexDirection: 'column',
                height: '100vh',
                overflow: 'hidden',
                maxHeight: '100vh'
            }}
        >
            {/* Header with close button */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: { xs: 1.5, sm: 2 },
                    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                    flexShrink: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.9)',
                    backdropFilter: 'blur(10px)'
                }}
            >
                <Typography variant="h6" sx={{
                    color: 'rgba(255, 255, 255, 0.9)',
                    fontWeight: 600,
                    fontSize: { xs: '1rem', sm: '1.1rem' },
                    textAlign: 'center',
                    flex: 1
                }}>
                    {currentCard?.name} {currentCard?.subtitle ? `- ${currentCard.subtitle}` : ''}
                </Typography>
                <IconButton
                    onClick={onClose}
                    sx={{
                        color: 'rgba(255, 255, 255, 0.8)',
                        ml: 1,
                        '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            color: 'white'
                        }
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </Box>

            {/* Card display area */}
            <Box
                sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: { xs: 1.5, sm: 2 },
                    position: 'relative',
                    overflow: 'hidden',
                    minHeight: 0,
                    height: '100%',
                    maxHeight: 'calc(100vh - 80px)'
                }}
                onTouchStart={handleTouchStart}
                onTouchEnd={handleTouchEnd}
            >
                {cards.map((card, index) => (
                    <Box
                        key={card.id}
                        sx={{
                            display: index === activeStep ? 'flex' : 'none',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            height: '100%',
                            width: '100%',
                            gap: 3,
                            minHeight: 0,
                            flex: 1,
                            overflow: 'hidden',
                            maxHeight: '100%'
                        }}
                    >
                        {card.image_urls && card.image_urls.length > 0 ? (
                            <img
                                src={card.image_urls[0].url}
                                alt={card.name}
                                style={{
                                    maxWidth: '100%',
                                    maxHeight: '55%',
                                    width: 'auto',
                                    height: 'auto',
                                    objectFit: 'contain',
                                    borderRadius: '20px',
                                    flexShrink: 0,
                                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)'
                                }}
                            />
                        ) : (
                            <Box
                                sx={{
                                    width: '280px',
                                    height: '180px',
                                    background: 'linear-gradient(135deg, rgba(76, 29, 149, 0.2), rgba(139, 92, 246, 0.2))',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    border: '2px dashed rgba(76, 29, 149, 0.3)',
                                    borderRadius: '20px',
                                    flexShrink: 0
                                }}
                            >
                                <Typography variant="h6" color="text.secondary">
                                    No image
                                </Typography>
                            </Box>
                        )}

                        {/* Card information below image - only for active card */}
                        <Box sx={{
                            p: { xs: 3, sm: 3.5 },
                            backgroundColor: 'transparent',
                            width: '100%',
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            mx: 'auto',
                            minHeight: 0,
                            overflow: 'hidden',
                            position: 'relative'
                        }}>
                            {/* Rating */}
                            <Box sx={{
                                mb: 3,
                                textAlign: 'center',
                                flexShrink: 0,
                                width: '100%',
                                position: 'relative',
                                zIndex: 1
                            }}>
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 2.5
                                }}>
                                    <Rating
                                        value={currentCard?.rating || 0}
                                        precision={0.5}
                                        size="medium"
                                        readOnly
                                        sx={{
                                            '& .MuiRating-iconFilled': { color: 'rgb(245, 158, 11)' },
                                            '& .MuiRating-iconHover': { color: 'rgb(245, 158, 11)' }
                                        }}
                                    />
                                    <Typography variant="h5" sx={{
                                        color: 'white',
                                        fontWeight: 800,
                                        fontSize: '1.4rem',
                                        textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)'
                                    }}>
                                        {currentCard?.rating || 0}
                                    </Typography>
                                </Box>
                            </Box>

                            {/* Description */}
                            <Box sx={{
                                textAlign: 'center',
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'flex-start',
                                alignItems: 'center',
                                minHeight: 0,
                                overflow: 'hidden',
                                width: '100%',
                                position: 'relative',
                                zIndex: 1
                            }}>
                                <Box sx={{
                                    flex: 1,
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    justifyContent: 'center',
                                    overflow: 'auto',
                                    maxHeight: '100%',
                                    minHeight: 0,
                                    width: '100%',
                                    px: 2,
                                    '&::-webkit-scrollbar': {
                                        width: '4px'
                                    },
                                    '&::-webkit-scrollbar-track': {
                                        background: 'rgba(255, 255, 255, 0.1)',
                                        borderRadius: '2px'
                                    },
                                    '&::-webkit-scrollbar-thumb': {
                                        background: 'rgba(255, 255, 255, 0.3)',
                                        borderRadius: '2px'
                                    }
                                }}>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: 'white',
                                            lineHeight: 1.7,
                                            fontSize: '1rem',
                                            wordBreak: 'break-word',
                                            hyphens: 'auto',
                                            textAlign: 'center',
                                            maxWidth: '100%',
                                            overflowWrap: 'break-word',
                                            fontWeight: 400,
                                            textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
                                        }}
                                    >
                                        {currentCard?.description || 'Brak opisu'}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                ))}

                {/* Navigation arrows */}
                {activeStep > 0 && (
                    <IconButton
                        onClick={handleBack}
                        sx={{
                            position: 'absolute',
                            left: 8,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            backgroundColor: 'transparent',
                            color: 'rgba(255, 255, 255, 0.8)',
                            zIndex: 10,
                            width: 48,
                            height: 48,
                            '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                color: 'white'
                            }
                        }}
                    >
                        <KeyboardArrowLeft sx={{ fontSize: '2rem' }} />
                    </IconButton>
                )}

                {activeStep < cards.length - 1 && (
                    <IconButton
                        onClick={handleNext}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            backgroundColor: 'transparent',
                            color: 'rgba(255, 255, 255, 0.8)',
                            zIndex: 10,
                            width: 48,
                            height: 48,
                            '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                color: 'white'
                            }
                        }}
                    >
                        <KeyboardArrowRight sx={{ fontSize: '2rem' }} />
                    </IconButton>
                )}
            </Box>
        </Box>
    );
};

export default MobileCardPreview; 